if (!customElements.get('quick-add-bulk')) {
  customElements.define(
    'quick-add-bulk',
    class QuickAddBulk extends BulkAdd {
      constructor() {
        super();
        this.quantity = this.querySelector('quantity-input');

        const debouncedOnChange = debounce((event) => {
          if (!Number.isSafeInteger(Number(event.target.value)) || !event.target.checkValidity()) {
            event.target.reportValidity();
            return;
          }
          if (parseInt(event.target.value) === 0) {
            this.startQueue(event.target.dataset.index, parseInt(event.target.value));
          } else {
            this.validateQuantity(event);
          }
        }, ON_CHANGE_DEBOUNCE_TIMER);

        this.addEventListener('change', debouncedOnChange.bind(this));
        this.listenForActiveInput();
        this.listenForKeydown();
        this.lastActiveInputId = null;
        const pageParams = new URLSearchParams(window.location.search);
        window.pageNumber = decodeURIComponent(pageParams.get('page') || '');
      }

      connectedCallback() {
        if (!this.getInput()) return;
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (
            event.source === 'quick-add' ||
            (event.cartData.items && !event.cartData.items.some((item) => item.id === parseInt(this.dataset.index))) ||
            (event.cartData.variant_id && !(event.cartData.variant_id === parseInt(this.dataset.index)))
          ) {
            return;
          }
          // If its another section that made the update
          this.onCartUpdate().then(() => {
            this.listenForActiveInput();
            this.listenForKeydown();
          });
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      getInput() {
        return this.querySelector('quantity-input input');
      }

      selectProgressBar() {
        return this.querySelector('.progress-bar-container');
      }

      listenForActiveInput() {
        if (!this.classList.contains('hidden')) {
          this.getInput()?.addEventListener('focusin', (event) => {
            this.lastActiveInputId = event.target.dataset.index;
            event.target.select();
          });
        }
        this.isEnterPressed = false;
      }

      listenForKeydown() {
        this.getInput()?.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            this.getInput().blur();
            this.isEnterPressed = true;
          }
        });
      }

      cleanErrorMessageOnType(event) {
        event.target.addEventListener(
          'keypress',
          () => {
            event.target.setCustomValidity('');
          },
          { once: true }
        );
      }

      onCartUpdate() {
        return new Promise((resolve, reject) => {
          fetch(`${this.getSectionsUrl()}?section_id=${this.closest('.collection').dataset.id}`)
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              const sourceQty = html.querySelector(
                `#quick-add-bulk-${this.dataset.id}-${this.closest('.collection').dataset.id}`
              );
              if (sourceQty) {
                this.innerHTML = sourceQty.innerHTML;
              }
              resolve();
            })
            .catch((e) => {
              console.error(e);
              reject(e);
            });
        });
      }

      showStatus(message) {
        const status = this.querySelector('[data-cart-quantity-status]');
        if (!status) return;
        status.textContent = message || '';
        status.hidden = !message;
      }

      async updateMultipleQty(items) {
        const progress = this.selectProgressBar();
        progress?.classList.remove('hidden');
        this.setAttribute('aria-busy', 'true');
        this.showStatus(this.dataset.updatingMessage);
        let confirmed = false;
        try {
          const ids = Object.keys(items);
          const body = JSON.stringify({
            updates: items,
            sections: this.getSectionsToRender().map((section) => section.section),
            sections_url: window.location.pathname + window.location.search,
          });
          const response = await fetch(routes.cart_update_url, { ...fetchConfig(), body });
          const state = await response.json();
          if (!response.ok || state.errors || !Array.isArray(state.items) || !Number.isSafeInteger(state.item_count)) throw Error();
          confirmed = true;
          this.renderSections(state, ids);
          const input = this.getInput();
          if (input && !this.queue.some((item) => String(item.id) === String(this.dataset.index))) {
            const quantity = state.items.filter((item) => String(item.variant_id || item.id) === String(this.dataset.index)).reduce((sum, item) => sum + item.quantity, 0);
            input.value = String(quantity);
            input.setAttribute('value', String(quantity));
            input.dataset.cartQuantity = String(quantity);
          }
          this.showStatus(this.dataset.savedMessage);
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add', cartData: state });
        } catch {
          this.queue = [];
          this.showStatus(confirmed ? this.dataset.savedMessage : this.dataset.errorMessage || window.cartStrings.error);
        } finally {
          this.selectProgressBar()?.classList.add('hidden');
          this.setAttribute('aria-busy', 'false');
          this.requestStarted = false;
        }
      }

      getSectionsToRender() {
        return [
          {
            id: `quick-add-bulk-${this.dataset.id}-${this.closest('.collection-quick-add-bulk').dataset.id}`,
            section: this.closest('.collection-quick-add-bulk').dataset.id,
            selector: `#quick-add-bulk-${this.dataset.id}-${this.closest('.collection-quick-add-bulk').dataset.id}`,
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
          {
            id: 'CartDrawer',
            selector: '#CartDrawer',
            section: 'cart-drawer',
          },
        ];
      }

      renderSections(parsedState, ids) {
        const intersection = this.queue.filter((element) => ids.includes(element.id));
        if (intersection.length !== 0) return;
        this.getSectionsToRender().forEach((section) => {
          const sectionElement = document.getElementById(section.id);
          if (
            sectionElement &&
            sectionElement.parentElement &&
            sectionElement.parentElement.classList.contains('drawer')
          ) {
            parsedState.items.length > 0
              ? sectionElement.parentElement.classList.remove('is-empty')
              : sectionElement.parentElement.classList.add('is-empty');

            setTimeout(() => {
              document.querySelector('#CartDrawer-Overlay').addEventListener('click', this.cart.close.bind(this.cart));
            });
          }
          const elementToReplace =
            sectionElement && sectionElement.querySelector(section.selector)
              ? sectionElement.querySelector(section.selector)
              : sectionElement;
          const sectionHTML = parsedState.sections?.[section.section];
          if (elementToReplace && typeof sectionHTML === 'string') {
            const source = new DOMParser().parseFromString(sectionHTML, 'text/html').querySelector(section.selector);
            if (source) elementToReplace.innerHTML = source.innerHTML;
          }
        });

        if (this.isEnterPressed) {
          this.querySelector(`#Quantity-${this.lastActiveInputId}`)?.select();
        }

        this.listenForActiveInput();
        this.listenForKeydown();
      }
    }
  );
}
